<template>
  <div>
    <!-- Show skeleton while loading -->
    <SidebarSkeleton
      v-if="isLoading"
      :is-collapsed="!this.$store.state.app.isExpandedMenu"
      :menu-items="8"
    />

    <!-- Show actual content when loaded -->
    <div v-else>
      <div class="sidebar-logo">
        <tooltip name="tooltip" :pointer="true" :event="'hover'"/>
        <router-link :to="homeUrl || '/'" class="text-lg" style="font-size: 20px">
          <span v-if="this.$store.state.app.isExpandedMenu" class="font-weight-600 text-slate-100"
                :style="logoPrefixStyle">{{
              branding.logo.prefix
            }} </span>
          <span v-if="this.$store.state.app.isExpandedMenu" class="font-weight-bold text-slate-500"
                :style="logoSuffixStyle">{{
              branding.logo.suffix
            }}</span>
          <span v-if="!this.$store.state.app.isExpandedMenu" class="font-weight-bold text-slate-500"
                :style="logoSuffixStyle">
           <i v-bind:class="$t('logo.icon')" style="padding: 8px"></i>
          </span>
        </router-link>
      </div>
      <div class="sidebar-inner" ref="formContainer">
        <hr style="margin: 15px 15px 0px;">
        <!-- Collapsed sidebar (minimized view) - shows icon with label below -->
        <section v-if="!this.$store.state.app.isExpandedMenu && !isMobile" class="ps-container scroll-area ps sidebar-scroll-container">
          <div id="sidebar-menu" class="sidebar-menu sidebar-menu-collapsed" style="padding-top: 0px !important;">
            <ul style="margin-top: 12px;">
              <li v-for="item in filteredNavItems"
                  :key="item.id"
                  v-if="isItemVisible(item)"
                  v-bind:class="{'active': isItemActive(item)}"
                  class="sidebar-item-collapsed">
                <router-link v-if="!item.isExternal" :to="item.path" class="sidebar-link-collapsed">
                  <i :class="item.icon" class="sidebar-icon-collapsed"></i>
                  <span class="sidebar-label-collapsed"> {{ item.translationKey ? $t(item.translationKey) : item.label }}</span>
                </router-link>
                <a v-else :href="posUrl" target="_blank" class="sidebar-link-collapsed">
                  <i :class="item.icon" class="sidebar-icon-collapsed"></i>
                  <span class="sidebar-label-collapsed"> {{ item.translationKey ? $t(item.translationKey) : item.label }}</span>
                </a>
              </li>
            </ul>
          </div>
          <div class="ps__rail-x" style="left: 0px; bottom: 0px;">
            <div class="ps__thumb-x" tabindex="0" style="left: 0px; width: 0px;"></div>
          </div>
          <div class="ps__rail-y" style="top: 0px; right: 0px;">
            <div class="ps__thumb-y" tabindex="0" style="top: 0px; height: 0px;"></div>
          </div>
        </section>

        <!-- Expanded sidebar (full width view) -->
        <section v-if="this.$store.state.app.isExpandedMenu || isMobile" class="ps-container scroll-area ps sidebar-scroll-container-expanded">
          <div id="sidebar-menu_mini" class="sidebar-menu" style="padding-top: 0px !important;">
            <ul style="margin-top: 12px;">
              <li v-for="item in filteredNavItems"
                  :key="item.id"
                  v-if="isItemVisible(item)"
                  v-bind:class="{'active': isItemActive(item)}">
                <router-link v-if="!item.isExternal" :to="item.path">
                  <i :class="item.icon"></i>
                  <span> {{ item.translationKey ? $t(item.translationKey) : item.label }}</span>
                </router-link>
                <a v-else :href="posUrl" target="_blank">
                  <i :class="item.icon"></i>
                  <span> {{ item.translationKey ? $t(item.translationKey) : item.label }}</span>
                </a>
              </li>
            </ul>
          </div>
          <div class="ps__rail-x" style="left: 0px; bottom: 0px;">
            <div class="ps__thumb-x" tabindex="0" style="left: 0px; width: 0px;"></div>
          </div>
          <div class="ps__rail-y" style="top: 0px; right: 0px;">
            <div class="ps__thumb-y" tabindex="0" style="top: 0px; height: 0px;"></div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import vueCustomScrollbar from 'vue-custom-scrollbar'
import "vue-custom-scrollbar/dist/vueScrollbar.css"
import {allowedModules} from "../../utils/modules";
import permissionService from '../../service/permission.service';
import navigationConfig from "../../config/sidebarNavigation";
import process from "shelljs";
import SidebarSkeleton from "../shared/SidebarSkeleton.vue";
import { getBrandingByDomain } from '../../config/domainBranding';

export default {
  components: {
    vueCustomScrollbar,
    SidebarSkeleton
  },
  data() {
    return {
      branding: getBrandingByDomain(location.host),
      isLoading: true, // Add loading state
      roleId: null,
      userId: null,
      selectedStoreId: null,
      permissions: {},
      // Cache permissions for better UX
      cachedPermissions: null,
      pagePermissions: [
        'DASHBOARD_PAYMENTS_READ',
        'AIRTIME_READ',
        'CUSTOMERS_READ',
        'CUSTOMER_READ',
        'STOCKS_READ',
        'INVENTORIES_READ',
        'QUOTATIONS_READ',
        'CATEGORIES_READ',
        'ORDERS_READ',
        'INVOICES_READ',
        'PAYMENTS_READ',
        'RECEIPTS_READ',
        'SUPPLIERS_READ',
        'SMS_READ',
        'EMAIL_READ',
        'VISITS_READ',
        'REPORTS_READ',
        'SETTINGS_READ',
        'INTEGRATIONS_READ',
        'POS_READ',
        'CHART_OF_ACCOUNTS_READ'
      ],
      modules: [],
      settings: {
        suppressScrollX: true,
      },
      activeClass: 'active',
      homeUrl: null,
      posUrl: null,
      currentIndustry: '',
      showLogoPrepender: false,
      isExpandedMenu: false,
      isDisabled: true,
    };
  },
  computed: {
    logoPrefixStyle() {
      return {
        color: this.branding.logo.prefixColor
      };
    },
    logoSuffixStyle() {
      return {
        color: this.branding.logo.suffixColor,
        fontWeight: 'bold',
        backgroundColor: this.branding.logo.suffixBackgroundColor,
        padding: '6px',
        borderRadius: '5px'
      };
    },
    // Filter navigation items based on industry, permissions, and modules
    filteredNavItems() {
      return navigationConfig.filter(item => {
        // Check if item is for current industry
        const industryMatch = item.industries.includes(this.currentIndustry);

        // Check if user has required permission (null means no permission required)
        const hasPermission = item.requiredPermission === null || this.permissions[item.requiredPermission];

        // Check if module is enabled
        const moduleEnabled = this.modules.some(module => module === item.id);

        return industryMatch && hasPermission && moduleEnabled;
      });
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentPath() {
      return this.$route.path;
    },
    allowedRoutes() {
      return JSON.parse(localStorage.getItem('allowedRoutes'))
    },
    salesChannels() {
      return JSON.parse(localStorage.getItem('salesChannels'))
    },
    isMobile() {
      return window.innerWidth <= 768;
    },
  },
  methods: {
    // Check if an item should be visible (not hidden)
    isItemVisible(item) {
      return !item.hidden;
    },

    isItemActive(item) {
      return item.activePaths.some(path => {
        // Exact match
        if (this.currentPath === path) return true;

        // Partial match for paths with wildcards or child routes
        if (path.endsWith('/') && this.currentPath.startsWith(path)) return true;
        
        // Match child routes (e.g., /credit-notes/123 matches /credit-notes)
        if (this.currentPath.startsWith(path + '/')) return true;
      });
    },

    handleSidebarState() {
      const isExpandedString = localStorage.getItem('isExpandedMenu');
      let isExpandedMenu = true; // default fallback

      if (isExpandedString !== null) {
        try {
          isExpandedMenu = JSON.parse(isExpandedString);
        } catch (e) {
          console.error('Failed to parse isExpandedMenu from localStorage:', e);
        }
      }

      if (isExpandedMenu) {
        document.body.classList.add('expand-menu');
        document.body.classList.remove('mini-sidebar');
      } else {
        document.body.classList.remove('expand-menu');
        document.body.classList.add('mini-sidebar');
      }

      localStorage.setItem('isExpandedMenu', JSON.stringify(isExpandedMenu));
      this.$store.dispatch('app/setIsExpandedMenu', isExpandedMenu);
    },

    async checkPermissions() {
      try {
        // Check if user is a customer contact
        const selectedContext = localStorage.getItem('selectedContext');
        let actorType = null;
        
        if (selectedContext) {
          try {
            const context = JSON.parse(selectedContext);
            actorType = context.actorType;
          } catch (e) {
            console.error('Failed to parse selectedContext');
          }
        }
        
        // For customer contacts, grant all permissions without API call
        if (actorType === 'CUSTOMER_CONTACT') {
          const customerPermissions = {};
          this.pagePermissions.forEach(permission => {
            customerPermissions[permission] = true;
          });
          this.permissions = customerPermissions;
          localStorage.setItem('userPermissions', JSON.stringify(customerPermissions));
        } else {
          // For admin users, check permissions normally
          const result = await permissionService.checkPermissions(this.pagePermissions);
          if (result.success) {
            this.permissions = result.data;
            // Cache for next time
            localStorage.setItem('userPermissions', JSON.stringify(result.data));
          }
        }
      } catch (error) {
        console.error('Permission check error:', error);
      } finally {
        this.isLoading = false;
      }
    },

    initializeOtherSettings() {
      // Setup home URL and POS URL
      this.homeUrl = localStorage.getItem('homeUrl');
      this.posUrl = process.env.POS_URL;

      // Determine current industry from locale
      const locale = this.$i18n.locale;
      const matched = allowedModules.find(function (module) {
        return module.name === locale;
      });

      this.currentIndustry = matched ? matched.name : 'commerce'; // Default to commerce if no match
      this.modules = matched && matched.modules ? matched.modules : [];
    },

    logout() {
      localStorage.clear()
      this.$router.push('/sign-in');
      location.reload();
    },

    handleNavigation(module, requiredPermission, path, event) {
      // Prevent the default navigation behavior
      event.preventDefault();

      if (!this.permissions.includes(requiredPermission)) {
        Swal.fire({
          icon: 'error',
          title: 'Access Denied',
          text: `You do not have permission to access ${module}.`,
          footer: '<a href="">Why do I have this issue?</a>'
        });
        // Stop further execution, keeping the user on the current page
        return;
      }

      // Allow navigation if the user has permission
      this.$router.push(path);
    },
  },

  async mounted() {
    // Start with loading state true
    this.isLoading = true;

    await this.handleSidebarState();
    await this.initializeOtherSettings();

    // Show cached permissions immediately if available
    const cached = localStorage.getItem('userPermissions');
    if (cached) {
      try {
        this.cachedPermissions = JSON.parse(cached);
        this.permissions = this.cachedPermissions;
        this.isLoading = false;
      } catch (e) {
        console.error('Invalid cached permissions');
      }
    }

    // Always fetch fresh permissions in background
    await this.checkPermissions();
  },
}
</script>

<style>
.disabled-link {
  pointer-events: none;
  cursor: not-allowed;
  opacity: 0.5;
}

/* Collapsed sidebar styles - compact layout */
.sidebar-menu-collapsed .sidebar-item-collapsed {
  text-align: center;
  margin-bottom: 0;
}

.sidebar-link-collapsed {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 6px 0 !important;
  gap: 3px;
  transition: all 0.2s ease;
}

.sidebar-link-collapsed:hover {
  background-color: transparent !important;
  background: transparent !important;
  border-radius: 0 !important;
  color: #8bc34a !important;
}

.sidebar-link-collapsed:hover .sidebar-icon-collapsed {
  color: #8bc34a !important;
}

.sidebar-link-collapsed:hover .sidebar-label-collapsed {
  color: #8bc34a !important;
}

.sidebar-item-collapsed:hover {
  background-color: transparent !important;
  background: transparent !important;
}

.sidebar-icon-collapsed {
  font-size: 16px !important;
  margin: 0 auto !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
}

/* Force label to be visible even with mini-sidebar global styles */
.mini-sidebar .sidebar-label-collapsed,
.sidebar-label-collapsed {
  font-size: 12px !important;
  line-height: 1.3 !important;
  text-align: center !important;
  display: block !important;
  visibility: visible !important;
  opacity: 0.95 !important;
  max-width: 100%;
  width: 100% !important;
  height: auto !important;
  position: static !important;
  font-weight: 500;
  letter-spacing: 0px;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  padding: 0 !important;
  margin: 0 !important;
  box-sizing: border-box !important;
}

/* Active state styling */
.sidebar-item-collapsed.active .sidebar-link-collapsed {
  background-color: transparent !important;
}

.sidebar-item-collapsed.active .sidebar-label-collapsed {
  color: #65cf21 !important;
  font-weight: 700 !important;
}

/* Scrollable container for collapsed sidebar */
.sidebar-scroll-container {
  max-height: 100vh !important;
  height: 100vh !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.sidebar-scroll-container::-webkit-scrollbar {
  width: 6px;
}

.sidebar-scroll-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.sidebar-scroll-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.sidebar-scroll-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Scrollable container for expanded sidebar */
.sidebar-scroll-container-expanded {
  max-height: 100vh !important;
  height: 100vh !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
}

.sidebar-scroll-container-expanded::-webkit-scrollbar {
  width: 6px;
}

.sidebar-scroll-container-expanded::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.sidebar-scroll-container-expanded::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.sidebar-scroll-container-expanded::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Mobile responsive: Force expanded view on mobile devices */
@media (max-width: 768px) {
  /* Hide collapsed sidebar on mobile */
  .mini-sidebar .sidebar-scroll-container {
    display: none !important;
  }
  
  /* Force expanded sidebar to show on mobile */
  body.mini-sidebar .sidebar-scroll-container-expanded {
    display: block !important;
  }
  
  /* Ensure sidebar takes full width on small screens */
  body.mini-sidebar .sidebar-inner {
    width: 100% !important;
  }
  
  /* Override mini-sidebar body class behavior on mobile */
  body.mini-sidebar {
    padding-left: 0 !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/layouts/SalesDueSidebar.vue